<template>
  <div class="quota-management-view">
    <!-- Header Section -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
      <div>
        <div class="d-flex align-items-center gap-2 mb-1">
          <span class="badge badge-soft-warning px-2 py-1">
            <i class="mdi mdi-account-group-outline mr-1"></i>{{ $currentLocale === 'id' ? 'Kapasitas SaaS' : 'SaaS Capacity' }}
          </span>
          <span class="text-muted small">{{ $currentLocale === 'id' ? 'Alokasi Langsung' : 'Live Allocation' }}</span>
        </div>
        <h2 class="text-white font-weight-bold mb-1">{{ $t('quota.title') }}</h2>
        <p class="text-muted small mb-0">
          {{ $t('quota.subtitle') }}
        </p>
      </div>
      <div class="d-flex gap-2">
        <router-link :to="{ name: 'QuotaAdd' }" class="btn btn-primary d-flex align-items-center gap-2">
          <i class="mdi mdi-plus-circle-outline"></i>
          <span>{{ $t('quota.topUpQuotaBtn') }}</span>
        </router-link>
      </div>
    </div>

    <!-- Telemetry Cards -->
    <div class="row g-3 mb-4">
      <div class="col-sm-6 col-xl-3">
        <div class="card card-glass h-100 p-3">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <span class="text-muted small text-uppercase font-weight-bold">{{ $t('quota.totalSeats') }}</span>
            <div class="stat-icon-badge bg-primary-soft text-primary">
              <i class="mdi mdi-account-multiple-check"></i>
            </div>
          </div>
          <div class="h3 font-weight-bold text-white mb-1">{{ totalAllocatedSeats }}</div>
          <div class="d-flex align-items-center text-muted small">
            <span class="text-info font-weight-medium mr-1">{{ companies.length }}</span> {{ $t('quota.tableCompany') }}
          </div>
        </div>
      </div>

      <div class="col-sm-6 col-xl-3">
        <div class="card card-glass h-100 p-3">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <span class="text-muted small text-uppercase font-weight-bold">{{ $t('quota.registeredPlayers') }}</span>
            <div class="stat-icon-badge bg-success-soft text-success">
              <i class="mdi mdi-account-arrow-right"></i>
            </div>
          </div>
          <div class="h3 font-weight-bold text-white mb-1">{{ totalActivePlayers }}</div>
          <div class="d-flex align-items-center text-muted small">
            <span class="text-success font-weight-medium mr-1">{{ activeCapacityPercent }}%</span> {{ $t('quota.seatsAllocated') }}
          </div>
        </div>
      </div>

      <div class="col-sm-6 col-xl-3">
        <div class="card card-glass h-100 p-3">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <span class="text-muted small text-uppercase font-weight-bold">{{ $t('quota.availableSeats') }}</span>
            <div class="stat-icon-badge bg-warning-soft text-warning">
              <i class="mdi mdi-seat-passenger"></i>
            </div>
          </div>
          <div class="h3 font-weight-bold text-white mb-1">{{ availableSeats }}</div>
          <div class="d-flex align-items-center text-muted small">
            {{ $t('quota.seatsAvailableForOnboarding') }}
          </div>
        </div>
      </div>

      <div class="col-sm-6 col-xl-3">
        <div class="card card-glass h-100 p-3">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <span class="text-muted small text-uppercase font-weight-bold">{{ $t('quota.avgUtilization') }}</span>
            <div class="stat-icon-badge bg-cyan-soft text-cyan">
              <i class="mdi mdi-chart-donut"></i>
            </div>
          </div>
          <div class="h3 font-weight-bold text-white mb-1">{{ activeCapacityPercent }}%</div>
          <div class="progress progress-sm mt-1" style="height: 6px;">
            <div
              class="progress-bar"
              :class="activeCapacityPercent > 85 ? 'bg-danger' : activeCapacityPercent > 60 ? 'bg-warning' : 'bg-success'"
              :style="{ width: `${activeCapacityPercent}%` }"
            ></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Table Container -->
    <div class="card card-glass">
      <div class="card-body">
        <!-- Filter and Search Bar -->
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
          <div class="search-box" style="min-width: 320px; max-width: 440px; width: 100%;">
            <div class="input-group">
              <span class="input-group-text bg-transparent border-end-0">
                <i class="mdi mdi-magnify text-muted"></i>
              </span>
              <input
                v-model="searchQuery"
                type="text"
                class="form-control border-start-0"
                :placeholder="$t('quota.searchPlaceholder')"
              />
              <button v-if="searchQuery" class="btn btn-outline-secondary border-start-0" type="button" @click="searchQuery = ''">
                <i class="mdi mdi-close"></i>
              </button>
            </div>
          </div>

          <div class="d-flex align-items-center gap-2">
            <select v-model="filterStatus" class="form-select custom-select bg-dark text-light border-secondary">
              <option value="all">{{ $t('quota.allCapacityStatus') }}</option>
              <option value="normal">{{ $t('quota.capacityAvailable') }}</option>
              <option value="warning">{{ $t('quota.capacityWarning') }}</option>
              <option value="critical">{{ $t('quota.capacityCritical') }}</option>
            </select>
          </div>
        </div>

        <!-- Table Loading State -->
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">{{ $t('common.loading') }}</span>
          </div>
          <p class="text-muted small mt-2">{{ $t('quota.loadingQuota') }}</p>
        </div>

        <!-- Table Data -->
        <div v-else-if="filteredCompanies.length > 0" class="table-responsive">
          <table class="table align-middle" style="table-layout: fixed; width: 100%;">
            <thead>
              <tr>
                <th style="width: 45px;">{{ $t('quota.tableNo') }}</th>
                <th style="width: 260px;">{{ $t('quota.tableCompany') }}</th>
                <th style="width: 160px;">{{ $t('quota.tablePackage') }}</th>
                <th style="width: 250px;">{{ $t('quota.tableUtilization') }}</th>
                <th style="width: 130px;" class="text-center">{{ $t('quota.tableStatus') }}</th>
                <th style="width: 120px; padding-right: 1rem;" class="text-end">{{ $t('quota.tableAction') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in filteredCompanies" :key="item.id">
                <td class="text-muted">{{ index + 1 }}</td>
                <td>
                  <div class="d-flex align-items-center gap-2">
                    <div class="company-avatar-box flex-shrink-0">
                      <i class="mdi mdi-domain text-warning"></i>
                    </div>
                    <div class="overflow-hidden">
                      <div class="font-weight-semibold text-white text-truncate" :title="(item.company && item.company.name) || item.name || $t('quota.tableCompany')">
                        {{ (item.company && item.company.name) || item.name || $t('quota.tableCompany') }}
                      </div>
                      <div class="text-muted small text-truncate" :title="item.email">
                        {{ $currentLocale === 'id' ? 'Pemilik:' : 'Owner:' }} {{ item.email }}
                      </div>
                    </div>
                  </div>
                </td>
                <td>
                  <span class="badge badge-soft-cyan font-weight-medium text-truncate d-inline-block" style="max-width: 150px;">
                    {{ (item.company && item.company.subscription) || 'Enterprise Unlimited' }}
                  </span>
                </td>
                <td>
                  <div class="d-flex align-items-center justify-content-between mb-1">
                    <span class="small text-white font-weight-medium">
                      {{ item.players_count || 0 }} / {{ (item.company && item.company.number_of_players) || 100 }} <span class="text-muted">{{ $t('quota.seats') }}</span>
                    </span>
                    <span
                      class="small font-weight-bold"
                      :class="getCapacityClass(item)"
                    >
                      {{ getUsagePercent(item) }}%
                    </span>
                  </div>
                  <div class="progress progress-sm" style="height: 6px;">
                    <div
                      class="progress-bar"
                      :class="getProgressBarClass(item)"
                      :style="{ width: `${getUsagePercent(item)}%` }"
                    ></div>
                  </div>
                </td>
                <td class="text-center">
                  <span
                    class="badge"
                    :class="getStatusBadgeClass(item)"
                  >
                    {{ getStatusText(item) }}
                  </span>
                </td>
                <td class="text-end">
                  <div class="d-inline-flex align-items-center gap-1">
                    <button
                      type="button"
                      class="btn btn-action-icon btn-outline-warning"
                      @click="openTopupModal(item)"
                      :title="$t('quota.topUpBtn')"
                    >
                      <i class="mdi mdi-plus-box"></i>
                    </button>
                    <router-link
                      :to="{ name: 'DetailOwner', params: { id: item.id } }"
                      class="btn btn-action-icon btn-outline-info"
                      :title="$t('common.view')"
                    >
                      <i class="mdi mdi-eye"></i>
                    </router-link>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty State -->
        <div v-else class="py-5">
          <EmptyState
            icon="mdi-account-group-outline"
            :title="$t('quota.noCompaniesFound')"
            :description="$currentLocale === 'id' ? 'Tidak ada perusahaan yang cocok dengan filter atau kata kunci pencarian Anda.' : 'No companies match your search query or capacity filter.'"
          />
        </div>
      </div>
    </div>

    <!-- Quick Top-up Modal -->
    <div v-if="isModalOpen" class="modal-backdrop-custom">
      <div class="modal-dialog-custom card card-glass">
        <div class="modal-header-custom d-flex justify-content-between align-items-center p-3 border-bottom border-secondary">
          <div class="d-flex align-items-center gap-2">
            <div class="stat-icon-badge bg-warning-soft text-warning">
              <i class="mdi mdi-account-plus"></i>
            </div>
            <div>
              <h5 class="text-white font-weight-bold mb-0">{{ $t('quota.expansionTitle') }}</h5>
              <small class="text-muted">{{ activeCompany?.company?.name || activeCompany?.name }}</small>
            </div>
          </div>
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="closeModal">
            <i class="mdi mdi-close"></i>
          </button>
        </div>

        <div class="modal-body-custom p-4">
          <div class="p-3 mb-3 rounded" style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.06);">
            <div class="row text-center">
              <div class="col-4 border-end border-secondary">
                <div class="text-muted small">{{ $t('quota.activeQuota') }}</div>
                <div class="h5 font-weight-bold text-white mb-0">{{ currentSeats }} {{ $t('quota.seats') }}</div>
              </div>
              <div class="col-4 border-end border-secondary">
                <div class="text-muted small">{{ $t('quota.registeredPlayers') }}</div>
                <div class="h5 font-weight-bold text-info mb-0">{{ activeCompany?.players_count || 0 }}</div>
              </div>
              <div class="col-4">
                <div class="text-muted small">{{ $t('quota.newQuotaEstimate') }}</div>
                <div class="h5 font-weight-bold text-success mb-0">{{ newCalculatedSeats }} {{ $t('quota.seats') }}</div>
              </div>
            </div>
          </div>

          <label class="form-label text-light small font-weight-bold mb-2">{{ $t('quota.additionalSeatsCount') }}</label>
          <div class="d-flex gap-2 mb-3">
            <button
              v-for="amt in [10, 25, 50, 100]"
              :key="amt"
              type="button"
              class="btn btn-sm flex-fill"
              :class="additionalSeats === amt ? 'btn-primary' : 'btn-outline-secondary'"
              @click="additionalSeats = amt"
            >
              +{{ amt }} {{ $t('quota.seats') }}
            </button>
          </div>

          <div class="mb-3">
            <label class="form-label text-light small font-weight-bold">{{ $t('quota.customSeatsLabel') }}</label>
            <div class="input-group">
              <input
                v-model.number="additionalSeats"
                type="number"
                min="1"
                max="1000"
                class="form-control bg-dark text-white border-secondary"
                :placeholder="$t('quota.customSeatsPlaceholder')"
              />
              <span class="input-group-text bg-dark text-muted border-secondary">{{ $t('quota.playerSeats') }}</span>
            </div>
          </div>

          <div class="alert alert-info py-2 px-3 small border-0" style="background: rgba(6, 182, 212, 0.1); color: #67e8f9;">
            <i class="mdi mdi-information-outline mr-1"></i>
            {{ $t('quota.newQuotaEstimate') }}: <strong>{{ newCalculatedSeats }} {{ $t('quota.seats') }}</strong>.
          </div>
        </div>

        <div class="modal-footer-custom d-flex justify-content-end gap-2 p-3 border-top border-secondary">
          <button type="button" class="btn btn-secondary px-3" @click="closeModal" :disabled="submitting">
            {{ $t('common.cancel') }}
          </button>
          <button type="button" class="btn btn-primary px-4 d-flex align-items-center gap-2" @click="submitTopup" :disabled="submitting || additionalSeats <= 0">
            <span v-if="submitting" class="spinner-border spinner-border-sm" role="status"></span>
            <i v-else class="mdi mdi-check"></i>
            <span>{{ $t('common.save') }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import config from '../config/config';
import store from '../store';
import Swal from 'sweetalert2';
import EmptyState from '@/components/EmptyState.vue';

export default {
  name: 'QuotaView',
  components: {
    EmptyState
  },
  data() {
    return {
      companies: [],
      loading: true,
      submitting: false,
      searchQuery: '',
      filterStatus: 'all',
      isModalOpen: false,
      activeCompany: null,
      additionalSeats: 25
    };
  },
  computed: {
    totalAllocatedSeats() {
      return this.companies.reduce((acc, curr) => {
        const seats = curr.company?.number_of_players || 100;
        return acc + parseInt(seats);
      }, 0);
    },
    totalActivePlayers() {
      return this.companies.reduce((acc, curr) => {
        return acc + (curr.players_count || 0);
      }, 0);
    },
    availableSeats() {
      return Math.max(0, this.totalAllocatedSeats - this.totalActivePlayers);
    },
    activeCapacityPercent() {
      if (this.totalAllocatedSeats === 0) return 0;
      return Math.min(100, Math.round((this.totalActivePlayers / this.totalAllocatedSeats) * 100));
    },
    filteredCompanies() {
      return this.companies.filter((item) => {
        const companyName = (item.company?.name || item.name || '').toLowerCase();
        const ownerEmail = (item.email || '').toLowerCase();
        const matchesSearch = companyName.includes(this.searchQuery.toLowerCase()) ||
                              ownerEmail.includes(this.searchQuery.toLowerCase());

        if (!matchesSearch) return false;

        const percent = this.getUsagePercent(item);
        if (this.filterStatus === 'normal') return percent < 70;
        if (this.filterStatus === 'warning') return percent >= 70 && percent <= 90;
        if (this.filterStatus === 'critical') return percent > 90;

        return true;
      });
    },
    currentSeats() {
      if (!this.activeCompany) return 0;
      return parseInt(this.activeCompany.company?.number_of_players || 100);
    },
    newCalculatedSeats() {
      return this.currentSeats + (parseInt(this.additionalSeats) || 0);
    }
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const token = store.state.authToken || localStorage.getItem('authToken') || localStorage.getItem('token');
        let resp;
        try {
          resp = await axios.get(`${config.API_URL}company`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
        } catch (fallbackErr) {
          resp = await axios.get(`${config.API_URL}company-list`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
        }
        const list = Array.isArray(resp.data) ? resp.data : (resp.data?.data || []);
        this.companies = Array.isArray(list) ? list : [];
      } catch (err) {
        console.error('Failed to fetch quota data:', err);
        Swal.fire({
          icon: 'error',
          title: this.$t('common.error'),
          text: err.response?.data?.message || (this.$currentLocale === 'id' ? 'Gagal memuat data kuota perusahaan.' : 'Failed to fetch enterprise quota data.'),
          background: '#12141c',
          color: '#fff'
        });
      } finally {
        this.loading = false;
      }
    },
    getUsagePercent(item) {
      const seats = parseInt(item.company?.number_of_players || 100);
      if (!seats) return 0;
      const players = parseInt(item.players_count || 0);
      return Math.min(100, Math.round((players / seats) * 100));
    },
    getCapacityClass(item) {
      const p = this.getUsagePercent(item);
      if (p > 90) return 'text-danger';
      if (p >= 70) return 'text-warning';
      return 'text-success';
    },
    getProgressBarClass(item) {
      const p = this.getUsagePercent(item);
      if (p > 90) return 'bg-danger';
      if (p >= 70) return 'bg-warning';
      return 'bg-success';
    },
    getStatusBadgeClass(item) {
      const p = this.getUsagePercent(item);
      if (p >= 100) return 'badge-soft-rose';
      if (p >= 75) return 'badge-soft-amber';
      return 'badge-soft-emerald';
    },
    getStatusText(item) {
      const p = this.getUsagePercent(item);
      if (p >= 90) return this.$t('quota.capacityCritical');
      if (p >= 70) return this.$t('quota.capacityWarning');
      return this.$t('quota.capacityAvailable');
    },
    openTopupModal(item) {
      this.activeCompany = item;
      this.additionalSeats = 25;
      this.isModalOpen = true;
    },
    closeModal() {
      this.isModalOpen = false;
      this.activeCompany = null;
    },
    async submitTopup() {
      if (!this.activeCompany) return;
      this.submitting = true;
      try {
        const token = store.state.authToken;
        const newTotal = this.newCalculatedSeats;
        
        await axios.put(`${config.API_URL}company/${this.activeCompany.id}`, {
          numberOfPlayers: newTotal
        }, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (this.activeCompany.company) {
          this.activeCompany.company.number_of_players = newTotal;
        }

        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: this.$t('superadmin.quickTopupSuccess') + ` (${newTotal} ${this.$t('quota.seats')})`,
          showConfirmButton: false,
          timer: 3000,
          background: '#12141c',
          color: '#fff'
        });

        this.closeModal();
      } catch (err) {
        console.error('Failed to update quota:', err);
        Swal.fire({
          icon: 'error',
          title: this.$t('common.error'),
          text: err.response?.data?.message || err.response?.data?.error || (this.$currentLocale === 'id' ? 'Gagal memperbarui alokasi kuota.' : 'Failed to update quota allocation.'),
          background: '#12141c',
          color: '#fff'
        });
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.quota-management-view {
  color: #e2e8f0;
}

.card-glass {
  background: rgba(15, 23, 42, 0.75);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
}

.stat-icon-badge {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.bg-primary-soft { background: rgba(59, 130, 246, 0.15); }
.bg-success-soft { background: rgba(16, 185, 129, 0.15); }
.bg-warning-soft { background: rgba(245, 158, 11, 0.15); }
.bg-cyan-soft { background: rgba(6, 182, 212, 0.15); }

.company-avatar-box {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.badge-soft-emerald {
  background: rgba(16, 185, 129, 0.15);
  color: #34d399;
  border: 1px solid rgba(16, 185, 129, 0.25);
}

.badge-soft-amber {
  background: rgba(245, 158, 11, 0.15);
  color: #fbbf24;
  border: 1px solid rgba(245, 158, 11, 0.25);
}

.badge-soft-rose {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
  border: 1px solid rgba(239, 68, 68, 0.25);
}

.badge-soft-cyan {
  background: rgba(6, 182, 212, 0.15);
  color: #38bdf8;
  border: 1px solid rgba(6, 182, 212, 0.25);
}

.badge-soft-warning {
  background: rgba(245, 158, 11, 0.15);
  color: #f59e0b;
  border: 1px solid rgba(245, 158, 11, 0.25);
}

.modal-backdrop-custom {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.modal-dialog-custom {
  width: 100%;
  max-width: 520px;
  background: #0f172a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
}
.btn-action-icon {
  width: 28px !important;
  height: 28px !important;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  border-radius: 6px !important;
  font-size: 14px !important;
  line-height: 1 !important;
  transition: all 0.15s ease-in-out;
}
.btn-action-icon:hover {
  transform: translateY(-1px);
}
</style>